<template>
    <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing">

        <div
            class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25">
            <div class="d-sm-flex align-items-center">
                <a class="btn btn-secondary" @click="generateCSV">
                    <i class="fa fa-plus-circle"></i>
                    Télécharger modèle
                </a>
            </div>
            <div class="d-flex align-items-center gap-2 flex-wrap">
                <input type="file" class="form-control" @change="lireCSVConvertirEnCsv($event)" accept=".csv" />
                <button class="btn btn-primary" @click="importer">Importer les éléments électriques</button>
                <button
                    v-if="elementsCreesDepuisImport.length"
                    type="button"
                    class="btn btn-outline-danger"
                    @click="annulerDernierImport"
                >
                    Annuler le dernier import ({{ elementsCreesDepuisImport.length }} nouveau(x) lien(s))
                </button>
            </div>
        </div>

        <div class="card-body p-15 p-sm-20 p-md-25">
            <div class="table-responsive">
                <table class="table text-nowrap align-middle mb-0">
                    <thead>
                        <tr>
                            <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                                Code place
                            </th>
                            <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                                Nom
                            </th>
                            <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                                Prénom
                            </th>
                            <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                                Raison sociale
                            </th>
                            <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                                Élément
                            </th>
                            <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                                Quantité
                            </th>
                            <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                                Coût unitaire
                            </th>
                            <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                                Montant total
                            </th>
                            <th v-show="aMotif" scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                                Motif
                            </th>
                            <th scope="col"
                                class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0  pe-0">
                                ACTIONS
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(element, index) in elements" :key="index">
                            <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                                {{ element?.code }}
                            </td>
                            <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                                {{ element?.nom }}
                            </td>
                            <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                                {{ element?.prenom }}
                            </td>
                            <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                                {{ element?.raisonSocial }}
                            </td>
                            <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                                {{ element?.element }}
                            </td>
                            <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                                {{ element?.quantite }}
                            </td>
                            <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                                {{ element?.cout }}
                            </td>
                            <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                                {{ montantTotalLigne(element) }}
                            </td>
                            <td v-show="aMotif" class="shadow-none lh-1 fw-medium text-black-emphasis">
                                {{ element?.motif }}
                            </td>
                            <td class="shadow-none lh-1 fw-medium text-body-tertiary pe-0">
                                <div class="dropdown">
                                    <button class="btn btn-sm btn-primary" type="button" data-bs-toggle="dropdown"
                                        aria-expanded="false">
                                        Actions
                                    </button>
                                    <ul class="dropdown-menu">
                                        <li>
                                            <a class="dropdown-item d-flex align-items-center"
                                                href="javascript:void(0);" @click="suppression(element.code, elements)">
                                                <i class="flaticon-delete lh-1 me-8 position-relative top-1"></i>
                                                Supprimer
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="pagination-area d-md-flex mt-15 mt-sm-20 mt-md-25 justify-content-between align-items-center">
                <PaginationComponent :page="1" :totalPages="1" :totalElements="elements.length" :limit="elements.length"
                    @paginate="handlePaginate" />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import Swal from "sweetalert2";
import ApiService from "@/services/ApiService";
import { suppression, error, success } from "@/utils/utils";
import PaginationComponent from '@/components/Utilities/Pagination.vue';

export default defineComponent({
    name: "ImportationElementElectrique",
    components: {
        PaginationComponent,
    },
    setup() {
        const elements = ref<Array<any>>([]);
        const page = ref<number>(1);
        const limit = ref<number>(10);
        const totalPages = ref<number>(1);
        const totalElements = ref<number>(0);

        /** Affichage uniquement : quantité × coût unitaire (hors modèle CSV / payload API). */
        const montantTotalLigne = (row: any): string => {
            const parseNum = (v: unknown) => {
                const s = String(v ?? "").trim().replace(/\s/g, "").replace(",", ".");
                const n = parseFloat(s);
                return Number.isFinite(n) ? n : NaN;
            };
            const q = parseNum(row?.quantite);
            const c = parseNum(row?.cout);
            if (!Number.isFinite(q) || !Number.isFinite(c)) {
                return "—";
            }
            return (q * c).toLocaleString("fr-FR", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            });
        };

        const generateCSV = () => {
            const headers = ["code", "nom", "prenom", "raisonSocial", "element", "quantite", "cout"];
            const rows = elements.value.map((row) => [
                row.code,
                row.nom,
                row.prenom,
                row.raisonSocial,
                row.element,
                row.quantite,
                row.cout
            ]);

            let csvContent = "data:text/csv;charset=utf-8,"
                + headers.join(";") + "\n"
                + rows.map(e => e.join(";")).join("\n");

            const encodedUri = encodeURI(csvContent);
            const link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            link.setAttribute("download", "elements_electriques.csv");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        };

        const aMotif = ref(false);
        const elementsCreesDepuisImport = ref<{ id: number; code: string; element: string }[]>([]);

        const annulerDernierImport = async () => {
            const ids = elementsCreesDepuisImport.value.map((x) => x.id);
            if (!ids.length) return;
            const r = await Swal.fire({
                icon: "warning",
                title: "Annuler l'import ?",
                html: `Les <strong>${ids.length}</strong> dernier(s) lien(s) contrat–élément <strong>créé(s)</strong> seront retirés (suppression logique).`,
                showCancelButton: true,
                confirmButtonText: "Annuler l'import",
                cancelButtonText: "Fermer",
                confirmButtonColor: "#d33",
            });
            if (!r.isConfirmed) return;
            try {
                ApiService.setHeader();
                const res = await ApiService.post("importation/elements/annuler", { contratElementIds: ids });
                if (res.status === 200) {
                    elementsCreesDepuisImport.value = [];
                    success(res.data.message || "Import annulé.");
                }
            } catch (erre: any) {
                error(erre?.response?.data?.message || erre?.message || "Annulation impossible.");
            }
        };

        const importer = async () => {
            if (elements.value.length < 1) {
                Swal.fire({
                    icon: 'error',
                    title: 'Erreur',
                    text: 'La liste des lignes à importer est vide.',
                });
                return;
            }
            elementsCreesDepuisImport.value = [];
            try {
                ApiService.setHeader();
                const response = await ApiService.post("importation/elements", elements.value);
                if (response.status === 200) {
                    success(response.data.message);
                    const payload = response.data.data;
                    const retour = Array.isArray(payload?.rejets)
                        ? payload.rejets
                        : Array.isArray(payload)
                            ? payload
                            : [];
                    const crees = Array.isArray(payload?.elementsCrees) ? payload.elementsCrees : [];
                    elements.value = [];
                    if (retour.length > 0) {
                        aMotif.value = true;
                        elements.value = retour;
                        elementsCreesDepuisImport.value = crees;
                        Swal.fire({
                            icon: 'error',
                            title: 'Erreur',
                            text: 'Certaines lignes n\'ont pas été importées. Consultez le tableau pour le motif de chaque ligne.',
                        });
                    } else {
                        elementsCreesDepuisImport.value = crees;
                        if (crees.length === 0) {
                            await Swal.fire({
                                icon: "success",
                                title: "Import terminé",
                                text: "Contrats mis à jour (aucun nouveau lien créé).",
                            });
                        }
                    }
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Erreur',
                        text: 'Une erreur est survenue lors de l\'importation',
                    });
                }
            } catch (erre: any) {
                console.log("Erreur  ==> ", erre);
                const msg = erre?.response?.data?.message || erre?.message || 'Erreur réseau ou serveur.';
                error(msg);
            }
        };

        const suppression = (code: string, element: any) => {
            Swal.fire({
                icon: "warning",
                html: `<span> Vous êtes sur le point de supprimer la ligne ayant le code <strong style="color:red;"> ` + code + `. </strong> Etes-vous sûr ?</span>`,
                buttonsStyling: true,
                showCancelButton: true,
                confirmButtonText: "Retirer",
                cancelButtonText: `Annuler`,
                heightAuto: false,
                customClass: {
                    confirmButton: "btn btn-danger",
                },
            }).then((result) => {
                if (result.isConfirmed) {
                    for (let i = 0; i < element.length; i++) {
                        if (element[i].code === code) {
                            element.splice(i, 1);
                        }
                    }
                    Swal.fire({
                        title: 'Succès',
                        text: "Elément retiré du tableau",
                        icon: "success",
                        toast: true,
                        timer: 5000,
                        position: 'top-right',
                        showConfirmButton: false,
                    });
                } else if (result.isDenied) {
                    Swal.fire("La suppression n'est pas passée", "", "info");
                }
            });
        };

        const handlePaginate = (newPage: number) => {
            page.value = newPage;
        };

        const lireCSVConvertirEnCsv = async (event) => {
        console.log("Event CSV ==> ", event.target);
            const file = (event.target as HTMLInputElement)?.files?.[0];
            console.log("File CSV ==> ", file);
            const isCSV = file && (
                file.type === "text/csv" ||
                (file.name && file.name.toLowerCase().endsWith('.csv'))
            );
            if (!file || !isCSV) {
                Swal.fire({
                    icon: 'error',
                    title: 'Erreur',
                    text: 'Veuillez sélectionner un fichier CSV..',
                });
                (event.target as HTMLInputElement).value = '';
                return;
            }

            const requiredHeaders = ["code", "nom", "prenom", "raisonSocial", "element", "quantite", "cout"];
            const reader = new FileReader();
            reader.onload = (ev: ProgressEvent<FileReader>) => {
                const target = ev.target as FileReader;
                const csv = target.result as string;
                const lines = csv.split("\n");
                const headers = lines[0].split(";");
                const cleanedHeaders = headers.map(header => header.trim());

                const missingHeaders = requiredHeaders.filter(cleaned => !cleanedHeaders.includes(cleaned));
                if (missingHeaders.length > 0) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Erreur',
                        text: `Les en-têtes suivantes sont manquantes : ${missingHeaders.join(", ")}`,
                    });
                    (event.target as HTMLInputElement).value = '';
                    return;
                }

                const result: Array<Record<string, any>> = [];
                for (let i = 1; i < lines.length; i++) {
                    const obj: any = {};
                    const currentline = lines[i].split(";");

                    for (let j = 0; j < headers.length; j++) {
                        obj[headers[j].trim()] = currentline[j] ? (currentline[j]).toString().trim() : "";
                    }

                    if (obj.code && obj.code != "") {
                        result.push(obj);
                    }
                }
                totalElements.value = result.length;
                elements.value = result;
            };

            reader.readAsText(file, 'ISO-8859-1');
        };

        return {
            elements,
            lireCSVConvertirEnCsv,
            importer,
            suppression,
            totalElements,
            totalPages,
            page,
            limit,
            aMotif,
            handlePaginate,
            generateCSV,
            montantTotalLigne,
            elementsCreesDepuisImport,
            annulerDernierImport,
        };
    },
});
</script>
